import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  MenuItem,
  Alert,
  Divider,
  CircularProgress,
  Card,
  CardContent,
} from '@mui/material';
import { Email, Send, CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import { emailApi, clientApi } from '../services/api';
import { Client } from '../types';

const EmailSettings: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClient, setSelectedClient] = useState<string>('');
  const [testEmail, setTestEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await clientApi.getAll();
      setClients(response.data.data || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmail) {
      setError('Please enter an email address');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      await emailApi.sendTest(testEmail);

      setSuccess(`Test email sent successfully to ${testEmail}!`);
      setTestEmail('');
    } catch (error: any) {
      console.error('Error sending test email:', error);
      setError(error.response?.data?.message || 'Failed to send test email');
    } finally {
      setLoading(false);
    }
  };

  const handleTriggerReminders = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      await emailApi.triggerReminders();

      setSuccess('Reminder check triggered successfully! Emails will be sent to clients with upcoming deadlines.');
    } catch (error: any) {
      console.error('Error triggering reminders:', error);
      setError(error.response?.data?.message || 'Failed to trigger reminders');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Email Settings & Testing
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Test email functionality and manually trigger reminder emails
        </Typography>
      </Box>

      {success && (
        <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {error && (
        <Alert severity="error" icon={<ErrorIcon />} sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Test Email Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Email sx={{ color: 'white', fontSize: 24 }} />
            </Box>
            <Typography variant="h6" fontWeight="bold">
              Send Test Email
            </Typography>
          </Box>
          <Divider sx={{ mb: 3 }} />

          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Send a test email to verify your email configuration is working correctly.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Email Address"
              type="email"
              fullWidth
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="test@example.com"
              disabled={loading}
            />
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={20} /> : <Send />}
              onClick={handleSendTestEmail}
              disabled={loading || !testEmail}
              sx={{ minWidth: 150 }}
            >
              {loading ? 'Sending...' : 'Send Test'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Manual Trigger Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Send sx={{ color: 'white', fontSize: 24 }} />
            </Box>
            <Typography variant="h6" fontWeight="bold">
              Manual Reminder Trigger
            </Typography>
          </Box>
          <Divider sx={{ mb: 3 }} />

          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Manually trigger the reminder email check. This will send emails to all clients with deadlines 
            within 7 days or 30 days past their period end date.
          </Typography>

          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              Note: The automated scheduler runs daily at 9:00 AM. This button allows you to trigger 
              the check manually without waiting.
            </Typography>
          </Alert>

          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} /> : <Send />}
            onClick={handleTriggerReminders}
            disabled={loading}
            fullWidth
          >
            {loading ? 'Triggering...' : 'Trigger Reminder Check Now'}
          </Button>
        </CardContent>
      </Card>

      {/* Email Configuration Info */}
      <Paper sx={{ p: 4 }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Email Configuration
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Email Provider
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Resend (configured via .env)
          </Typography>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Automated Schedule
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Daily at 9:00 AM
          </Typography>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Reminder Types
          </Typography>
          <Typography variant="body2" color="textSecondary">
            • 1 week before period end date
          </Typography>
          <Typography variant="body2" color="textSecondary">
            • 1 month after period end date (overdue)
          </Typography>
        </Box>

        <Alert severity="warning" sx={{ mt: 3 }}>
          <Typography variant="body2">
            Make sure your RESEND_API_KEY is configured in the backend .env file for emails to work.
          </Typography>
        </Alert>
      </Paper>
    </Container>
  );
};

export default EmailSettings;